import { computed, ref } from "vue";
import { refDebounced } from "@vueuse/core";
import type { SatelliteContactSiteStats } from "@shopexperts/legacy-api";
import {
    useSatelliteContactSiteMutations,
    useSatelliteContactSites,
    useSatelliteContactSitesStats,
} from "./useSatelliteContactSites";

export type SatelliteActiveFilter = "all" | "active" | "inactive";

export function useSatelliteSitesPage() {
    const searchInput = ref("");
    const activeFilter = ref<SatelliteActiveFilter>("all");
    const debouncedSearch = refDebounced(searchInput, 300);

    const listQuery = computed(() => ({
        search: debouncedSearch.value.trim() || undefined,
        is_active: activeFilter.value === "all"
            ? undefined
            : activeFilter.value === "active",
    }));

    const {
        data: listData,
        isLoading: isListLoading,
        isFetching: isListFetching,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        error: listError,
    } = useSatelliteContactSites(listQuery);

    const {
        data: statsData,
        isLoading: isStatsLoading,
        error: statsError,
    } = useSatelliteContactSitesStats();

    const mutations = useSatelliteContactSiteMutations();

    const sites = computed(() => listData.value?.pages.flatMap((page) => page.data) ?? []);

    const statsBySiteId = computed(() => {
        const map = new Map<number, SatelliteContactSiteStats>();
        for (const site of statsData.value?.sites ?? []) {
            map.set(site.id, site.stats);
        }
        return map;
    });

    const aggregateStats = computed(() => statsData.value?.aggregate ?? null);

    return {
        searchInput,
        activeFilter,
        sites,
        statsBySiteId,
        aggregateStats,
        isListLoading,
        isListFetching,
        isStatsLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        listError,
        statsError,
        ...mutations,
    };
}
